/* React */
import React, { useState } from "react";

/* Ionic */
import { IonCard, IonIcon } from "@ionic/react";
import { downloadOutline } from "ionicons/icons";

/* Utils */
import { useResizeOrFallback } from "src/utils/hooks/charts/useResizeOrFallback";
import { downloadElement } from "src/utils/helper/charts/downloadSvg";

/* Components */
import ChartWrapperOptions from "src/utils/wrapper/chart-wrapper/chart-wrapper-options/ChartWrapperOptions";

/* Stylesheet */
import styles from "./ChartWrapper.module.scss";

interface ChartWrapperProps {
  scssProps?: ScssProps;
  label?: React.ReactNode;
  options?: React.ReactNode;
  legend?: React.ReactNode;
  withResize?: boolean;
  downloadFilename?: string;
  children: (
    height: number,
    width: number
  ) => React.ReactNode;
}

interface ScssProps {
  padding?: string;
  height?: string;
}

const ChartWrapper = ({
  scssProps,
  label,
  legend,
  options,
  downloadFilename,
  children,
}: ChartWrapperProps) => {
  const { containerRef, height, width } =
    useResizeOrFallback();
  const [isDownloading, setIsDownloading] =
    useState(false);
  // Ref-based guard flips synchronously so a second click that arrives
  // before React commits the isDownloading state update is still blocked.
  const isDownloadingRef = React.useRef(false);

  const handleDownload = async () => {
    const element = containerRef.current;
    if (!element || isDownloadingRef.current) return;

    isDownloadingRef.current = true;

    // Hide the action buttons without removing their layout space so the
    // exported PNG matches what the user sees (display:none shifts sibling elements).
    const actions = element.querySelector(
      `.${styles.actions}`
    ) as HTMLElement | null;
    const previousVisibility = actions?.style.visibility;
    const previousPointerEvents = actions?.style.pointerEvents;

    setIsDownloading(true);
    if (actions) {
      actions.style.visibility = "hidden";
      actions.style.pointerEvents = "none";
    }

    try {
      await downloadElement(
        element,
        downloadFilename ?? "chart"
      );
    } catch (error) {
      console.error("Failed to download chart", error);
    } finally {
      if (actions) {
        actions.style.visibility = previousVisibility ?? "";
        actions.style.pointerEvents = previousPointerEvents ?? "";
      }
      isDownloadingRef.current = false;
      setIsDownloading(false);
    }
  };

  return (
    <IonCard
      ref={containerRef as any}
      style={{
        padding: scssProps?.padding,
        height: scssProps?.height,
      }}
      className={styles.chart_wrapper}
    >
      <div className={styles.flex_container}>
        {label ? (
          <>{label}</>
        ) : (
          // If no labelComponent is provided, render an empty div for flex-layout
          <div />
        )}
        <div className={styles.actions}>
          {options ? (
            <ChartWrapperOptions options={options} />
          ) : undefined}
          {downloadFilename !== undefined ? (
            <button
              type="button"
              className={`${styles.download_button} ${isDownloading ? styles.download_button_loading : ""}`}
              onClick={handleDownload}
              disabled={isDownloading}
              aria-label="Download chart as PNG"
              title="Download as PNG"
            >
              <IonIcon icon={downloadOutline} />
            </button>
          ) : undefined}
        </div>
      </div>
      {/* TODO Check if component Toolbar is required anymore */}
      {/* {label && <Toolbar label={label} options={options} />} */}
      {children(height, width)}
      {/* TODO Check if component Legend is required anymore */}
      {/* {legend && <Legend legend={legend} />} */}
      {legend ? <>{legend}</> : undefined}
    </IonCard>
  );
};

export default ChartWrapper;
